import React, { useEffect, useRef } from 'react';

import { CHIP_SELECTOR_ID, DROPZONE_ID } from 'constants/ids';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import {
  getSetBusinessPlanFocusedField,
  getSetBusinessPlanFocusedFieldAttribute,
  getSetBusinessPlanFocusedFieldId,
} from 'store/useBusinessToolStore/selectors';

import { IFormFocusProviderProps } from './types';

const removeIndexFromName = (name: string) => {
  return name.replace(/\.(\d+)\./gi, '.');
};

export const FormFocusProvider: React.FC<IFormFocusProviderProps> = ({ children }) => {
  const formRef = useRef<HTMLFormElement>(null);

  const setBusinessPlanFocusedField = useBusinessToolStore(getSetBusinessPlanFocusedField);
  const setBusinessPlanFocusedFieldId = useBusinessToolStore(getSetBusinessPlanFocusedFieldId);
  const setBusinessPlanFocusedFieldAttribute = useBusinessToolStore(getSetBusinessPlanFocusedFieldAttribute);

  useEffect(() => {
    const form = formRef.current;
    if (!form) return;

    const handleFocus = (event: FocusEvent) => {
      const target = event.target as HTMLInputElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        (target.tagName === 'DIV' && target.dataset.id === DROPZONE_ID) ||
        (target.tagName === 'BUTTON' && target.dataset.id === CHIP_SELECTOR_ID)
      ) {
        const focusedFieldName = removeIndexFromName(target.name || target.dataset.name || '');
        setBusinessPlanFocusedField(focusedFieldName);
        const focusedFieldId = removeIndexFromName(target.id);
        setBusinessPlanFocusedFieldId(focusedFieldId);
        const focusedFieldAttribute = removeIndexFromName(target.dataset.mirrorField || '');
        setBusinessPlanFocusedFieldAttribute(focusedFieldAttribute);
      }
    };

    // Add the focus event listener to the form
    form.addEventListener('focus', handleFocus, true);

    // Return a cleanup function to remove the event listener
    return () => {
      form.removeEventListener('focus', handleFocus, true);
    };
  }, [setBusinessPlanFocusedField, setBusinessPlanFocusedFieldId, setBusinessPlanFocusedFieldAttribute]);

  return <form ref={formRef}>{children}</form>;
};
